import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { RutterItemRule } from '~hooks/useRutter/models';

interface ItemRuleSectionProps {
  isDisabled: boolean;
  isSuggesting: boolean;
  onChange: (instructions: string) => void;
  onDismissSuggestion: () => void;
  onSuggest: () => void;
  onUseSuggestion: (instructions: string) => void;
  placeholderKey: string;
  suggestion: RutterItemRule | null;
  titleKey: string;
  value: string;
}

export default function ItemRuleSection({
  isDisabled,
  isSuggesting,
  onChange,
  onDismissSuggestion,
  onSuggest,
  onUseSuggestion,
  placeholderKey,
  suggestion,
  titleKey,
  value,
}: ItemRuleSectionProps) {
  const backtest = suggestion?.suggestionBacktest ?? null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        mb: 3,
        p: 2,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600 }} variant="body1">
          {t(titleKey)}
        </Typography>
        <SmallButton
          color="secondary"
          data-test-id={`item-rule-suggest-${titleKey}`}
          disabled={isDisabled || isSuggesting}
          onClick={onSuggest}
          startIcon={<AutoAwesomeIcon fontSize="small" />}
          variant="outlined"
        >
          {isSuggesting
            ? t(
                'administration.integration.rutter.quick_books_online.item_rules.drafting',
              )
            : t(
                'administration.integration.rutter.quick_books_online.item_rules.suggest',
              )}
        </SmallButton>
      </Box>

      <TextField
        disabled={isDisabled}
        fullWidth
        minRows={4}
        multiline
        onChange={(event) => onChange(event.target.value)}
        placeholder={t(placeholderKey)}
        value={value}
      />

      {suggestion && !suggestion.suggestedInstructions && (
        <Box
          data-test-id="item-rule-no-suggestion"
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 1,
            mt: 1.5,
            p: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 0.5 }} variant="body2">
            {t(
              'administration.integration.rutter.quick_books_online.item_rules.suggestion.none_title',
            )}
          </Typography>
          <Typography variant="body2">{suggestion.suggestionReason}</Typography>
          <Box sx={{ mt: 1 }}>
            <SmallButton
              color="secondary"
              onClick={onDismissSuggestion}
              variant="outlined"
            >
              {t(
                'administration.integration.rutter.quick_books_online.item_rules.suggestion.dismiss',
              )}
            </SmallButton>
          </Box>
        </Box>
      )}

      {suggestion?.suggestedInstructions && (
        <Box
          data-test-id="item-rule-suggestion"
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 1,
            mt: 1.5,
            p: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 0.5 }} variant="body2">
            {t(
              'administration.integration.rutter.quick_books_online.item_rules.suggestion.title',
            )}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body2">
            {suggestion.suggestedInstructions}
          </Typography>
          {suggestion.suggestionReason && (
            <Typography sx={{ color: 'text.secondary', mt: 0.5 }} variant="caption">
              {t(
                'administration.integration.rutter.quick_books_online.item_rules.suggestion.reason',
                { reason: suggestion.suggestionReason },
              )}
            </Typography>
          )}
          {backtest && (
            <Typography
              sx={{ color: 'text.secondary', display: 'block' }}
              variant="caption"
            >
              {t(
                'administration.integration.rutter.quick_books_online.item_rules.suggestion.backtest',
                {
                  items: backtest.itemLines,
                  needsItem: backtest.needsItemLines,
                  notCovered: backtest.notCoveredLines,
                  tested: backtest.testedLines,
                  total: backtest.totalLines,
                },
              )}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <SmallButton
              color="brandV2Yellow"
              disabled={isDisabled}
              onClick={() => onUseSuggestion(suggestion.suggestedInstructions ?? '')}
            >
              {t(
                'administration.integration.rutter.quick_books_online.item_rules.suggestion.use',
              )}
            </SmallButton>
            <SmallButton
              color="secondary"
              onClick={onDismissSuggestion}
              variant="outlined"
            >
              {t(
                'administration.integration.rutter.quick_books_online.item_rules.suggestion.dismiss',
              )}
            </SmallButton>
          </Box>
        </Box>
      )}
    </Box>
  );
}
